import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, ShieldAlert, Sparkles } from "lucide-react";

import OnboardingPanelFooter from "features/courier-onboarding/components/OnboardingPanelFooter";
import CourierDocumentSlot, {
  type SlotExtraction,
} from "features/courier-onboarding/components/upload/CourierDocumentSlot";
import { useCourierOnboarding } from "features/courier-onboarding/context/CourierOnboardingContext";
import { checkRegistration } from "features/courier-onboarding/services/courierOnboardingService";
import {
  isRequiredDocumentReady,
  requiredDocsByTrack,
  type CourierDocumentKey,
} from "features/courier-onboarding/types";
import { scrollToInvalidField } from "features/courier-onboarding/utils/scrollToInvalidField";

interface Props {
  onValid: () => void;
  onBack: () => void;
  nextLabel?: string;
}

/** Whole years between `birthDate` (YYYY-MM-DD) and today, or null if unparsable. */
const computeAge = (birthDate: string | null | undefined): number | null => {
  if (!birthDate) return null;
  const m = birthDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const dob = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const beforeBirthday =
    now.getUTCMonth() < dob.getUTCMonth() ||
    (now.getUTCMonth() === dob.getUTCMonth() &&
      now.getUTCDate() < dob.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age;
};

const MIN_AGE = 16;

const DocumentsStep: React.FC<Props> = ({ onValid, onBack, nextLabel }) => {
  const { t } = useTranslation();
  const { data } = useCourierOnboarding();
  const [error, setError] = useState<string | null>(null);
  const [scrollNonce, setScrollNonce] = useState(0);

  const track = data.metadata.businessTrack ?? "LICENSED_BUSINESS";
  const requiredKeys = requiredDocsByTrack[track];

  // Extractions are read from shared state so the age / ID-mismatch guards
  // remain correct even when returning to this step from Review (the slot
  // writes each extraction into context on upload/replace/remove).
  const extractions = data.ocrExtractions;
  const [busyKeys, setBusyKeys] = useState<Set<CourierDocumentKey>>(new Set());
  const [duplicate, setDuplicate] = useState(false);

  const handleBusyChange = useCallback(
    (key: CourierDocumentKey, busy: boolean) => {
      setBusyKeys((prev) => {
        const next = new Set(prev);
        if (busy) next.add(key);
        else next.delete(key);
        return next;
      });
    },
    [],
  );

  const handleExtraction = useCallback(
    (_key: CourierDocumentKey, info: SlotExtraction | null) => {
      setError(null);
      // Early duplicate guard: as soon as a national ID is extracted, check it
      // against existing registrations. Phone is not yet known at this step, so
      // the phone match is enforced again at submit time.
      const taxId = info?.extractedIdNumber;
      if (taxId && taxId.length === 9) {
        void checkRegistration({ taxId })
          .then((res) => setDuplicate(res.exists))
          .catch(() => setDuplicate(false));
      }
    },
    [],
  );

  const isBusy = busyKeys.size > 0;

  const firstMissingKey = useMemo(
    () =>
      requiredKeys.find(
        (key) => !isRequiredDocumentReady(key, data.documents),
      ),
    [requiredKeys, data.documents],
  );

  const uploadedCount = useMemo(
    () =>
      requiredKeys.filter((key) =>
        isRequiredDocumentReady(key, data.documents),
      ).length,
    [requiredKeys, data.documents],
  );
  const allUploaded = uploadedCount === requiredKeys.length;

  // The birth date comes from the driver license OCR.
  const age = computeAge(extractions.driverLicense?.birthDate);
  const isUnderage = age !== null && age < MIN_AGE;

  // The Tax ID must match the bank account and business license IDs, but the
  // driver license ID is allowed to differ (the license holder is not required
  // to be the tax/bank account holder). The driver license is therefore
  // excluded from this consistency check.
  const idMismatch = useMemo(() => {
    const ids = requiredKeys
      .filter((key) => key !== "driverLicense")
      .map((key) => extractions[key]?.extractedIdNumber)
      .filter((id): id is string => !!id && id.length === 9);
    if (data.personalInfo.taxId && data.personalInfo.taxId.length === 9) {
      ids.push(data.personalInfo.taxId);
    }
    return new Set(ids).size > 1;
  }, [requiredKeys, extractions, data.personalInfo.taxId]);

  const handleNext = () => {
    if (!allUploaded) {
      const profile = data.documents.find((d) => d.key === "profilePicture");
      setError(
        firstMissingKey === "profilePicture" &&
          profile?.profileCheckStatus === "rejected"
          ? "onboarding.errors.profilePhotoRejected"
          : "onboarding.errors.documentsMissing",
      );
      setScrollNonce((n) => n + 1);
      return;
    }
    if (isBusy) {
      setError("onboarding.errors.ocrInProgress");
      return;
    }
    if (isUnderage) {
      setError("onboarding.errors.underage");
      return;
    }
    if (idMismatch) {
      setError("onboarding.errors.documentsIdMismatch");
      return;
    }
    if (duplicate) {
      setError("onboarding.errors.alreadyRegistered");
      return;
    }
    setError(null);
    onValid();
  };

  useEffect(() => {
    if (
      (error !== "onboarding.errors.documentsMissing" &&
        error !== "onboarding.errors.profilePhotoRejected") ||
      !firstMissingKey
    ) {
      return;
    }
    scrollToInvalidField(firstMissingKey);
  }, [error, firstMissingKey, scrollNonce]);

  return (
    <div className="flex flex-col gap-5">
      <section className="flex items-start gap-3 border-s-2 border-[#bf0035] bg-[#bf0035]/[0.03] px-4 py-3">
        <Sparkles
          size={17}
          className="mt-0.5 shrink-0 text-[#bf0035]"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm leading-relaxed text-gray-700">
            {t("onboarding.documents.multiHelper", {
              count: requiredKeys.length,
            })}
          </p>
          <span className="mt-2 inline-flex rounded-full bg-[#bf0035]/[0.07] px-2.5 py-1 text-[11px] font-semibold text-[#bf0035]">
            {t("onboarding.documents.uploadProgress", {
              uploaded: uploadedCount,
              total: requiredKeys.length,
            })}
          </span>
        </div>
      </section>

      <div className="divide-y divide-gray-200">
        {requiredKeys.map((key) => (
          <CourierDocumentSlot
            key={key}
            docKey={key}
            required
            onExtraction={(info) => handleExtraction(key, info)}
            onBusyChange={handleBusyChange}
          />
        ))}
      </div>

      {isUnderage && (
        <div className="rounded-2xl border border-haat-red-200 bg-haat-red-50 p-4 flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-white text-haat-red-700 flex items-center justify-center flex-shrink-0">
            <ShieldAlert size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-haat-red-900">
              {t("onboarding.errors.underageTitle")}
            </p>
            <p className="text-xs text-haat-red-800 mt-0.5">
              {t("onboarding.errors.underage", { minAge: MIN_AGE })}
            </p>
          </div>
        </div>
      )}

      {idMismatch && !isUnderage && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-white text-amber-600 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {t("onboarding.errors.documentsIdMismatchTitle")}
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              {t("onboarding.errors.documentsIdMismatch")}
            </p>
          </div>
        </div>
      )}

      {duplicate && (
        <div className="rounded-2xl border border-haat-red-200 bg-haat-red-50 p-4 flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-white text-haat-red-700 flex items-center justify-center flex-shrink-0">
            <ShieldAlert size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-haat-red-900">
              {t("onboarding.errors.alreadyRegisteredTitle")}
            </p>
            <p className="text-xs text-haat-red-800 mt-0.5">
              {t("onboarding.errors.alreadyRegistered")}
            </p>
            <Link
              to="/login"
              className="inline-flex mt-2 text-xs font-semibold text-haat-red-700 underline underline-offset-2"
            >
              {t("onboarding.errors.alreadyRegisteredCta")}
            </Link>
          </div>
        </div>
      )}

      {error && !isUnderage && !idMismatch && !duplicate ? (
        <span className="text-xs font-medium text-haat-red-700">{t(error)}</span>
      ) : null}

      <OnboardingPanelFooter>
        <div className="flex flex-col gap-2">
          {error && !isUnderage && !idMismatch && !duplicate ? (
            <p className="text-xs font-medium text-haat-red-700">{t(error)}</p>
          ) : null}
          <div className="flex justify-between gap-3">
          <button type="button" onClick={onBack} className="btn-outline">
            {t("common.back")}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={isBusy || isUnderage || idMismatch || duplicate}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {isBusy ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {t("onboarding.documents.ocrInProgressShort")}
              </>
            ) : (
              (nextLabel ?? t("common.next"))
            )}
          </button>
        </div>
        </div>
      </OnboardingPanelFooter>
    </div>
  );
};

export default DocumentsStep;
